import * as dialog from '@zag-js/dialog';
import { normalizeProps, useMachine } from '@zag-js/solid';
import {
  DataProvider,
  EventBusProvider,
  Icon,
  Node,
  spec,
  specPatch,
  specTrack,
  specUpdate,
  ThemeProvider,
  useEventBus,
  useTheme,
} from 'framework';
import { assertRootNotDerived, DerivedResolutionError } from './derive';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  splitProps,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { Portal } from 'solid-js/web';
import { compareObjects } from 'utils';
import classes from './renderer.module.css';

const HeaderBar = (props) => {
  const theme = useTheme();
  return (
    <div class={classes.topbar}>
      <Show when={props.logo}>
        <img src={props.logo} class={classes.logo} />
      </Show>
      <Show when={props.title}>
        <h1 class={classes.title}>{props.title}</h1>
      </Show>
    </div>
  );
};

const matchesAnyTrigger = (event, triggers) => {
  if (!event || !triggers) return false;
  const list = unwrap(triggers);
  if (!list?.length) return false;
  return list.some((t) => compareObjects(event, t));
};

const PanelNode = (props) => {
  const theme = useTheme();
  const { triggerEvent } = useEventBus();
  const service = useMachine(dialog.machine, {
    id: createUniqueId(),
    modal: true,
    defaultOpen: false,
    onOpenChange(details) {
      if (details.open) props.onOpen();
      else props.onClose();

      triggerEvent({
        action: details.open ? 'open' : 'close',
        source: `/panels/${props.id}`,
        data: null,
      });
    },
  });
  const api = createMemo(() => dialog.connect(service, normalizeProps));
  createEffect(
    on(
      () => props.open,
      () => {
        if (props.open !== api().open) api().setOpen(props.open);
      },
    ),
  );

  const align =
    props.position?.top || props.position?.bottom ? 'start' : 'center';
  const justify =
    props.position?.left || props.position?.right ? 'start' : 'center';

  const [nodeId, nodeSpec] = splitProps(props.node, ['id', 'nodeType']);

  return (
    <Show when={api().open}>
      <Portal mount={document.querySelector('#eidos')}>
        <div {...api().getBackdropProps()} />
        <div
          {...api().getPositionerProps()}
          style={{
            ...api().getPositionerProps().style,
            position: 'absolute',
            'align-items': align,
            'justify-content': justify,
          }}
        >
          <div
            {...api().getContentProps()}
            style={{
              ...api().getContentProps().style,
              height: props.height
                ? typeof props.height === 'number'
                  ? `${props.height}px`
                  : props.height
                : '80%',
              width: props.width
                ? typeof props.width === 'number'
                  ? `${props.width}px`
                  : props.width
                : '80%',
              'z-index': 100,
              ...(props.position && {
                position: 'absolute',
                ...Object.fromEntries(
                  Object.entries(props.position)
                    .filter(([, v]) => v != null)
                    .map(([k, v]) => [k, typeof v === 'number' ? `${v}px` : v]),
                ),
              }),
            }}
          >
            <Show when={props.title || props.closable !== false}>
              <div class={classes.panelHeader}>
                <Show when={props.title}>
                  <span {...api().getTitleProps()} class={classes.panelTitle}>
                    {props.title}
                  </span>
                </Show>
                <Show when={props.closable !== false}>
                  <button
                    {...api().getCloseTriggerProps()}
                    class={classes.panelClose}
                  >
                    <Icon
                      url="icon://x-circle"
                      size={20}
                      color={theme().textColor}
                    />
                  </button>
                </Show>
              </div>
            </Show>
            <div
              style={{
                display: 'flex',
                'flex-direction': 'column',
                flex: 1,
                'min-height': 0,
                overflow: 'auto',
              }}
            >
              <Node
                id={nodeId.id}
                nodeSpec={nodeSpec}
                nodeType={nodeId.nodeType}
                path={`/panels/#${props.id}/node`}
              />
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
};

export const Eidos = (props) => {
  const { lastEvent, triggerEvent } = useEventBus();
  const [panelOpen, setPanelOpen] = createSignal([]);
  const [rootError, setRootError] = createSignal<string | null>(null);

  // EID-47: A derived node at the spec root has no source to resolve against
  // and is rejected by the renderer rather than silently rendering nothing.
  createEffect(() => {
    try {
      assertRootNotDerived(spec.root);
      setRootError(null);
    } catch (err) {
      setRootError(
        err instanceof DerivedResolutionError ? err.message : String(err),
      );
    }
  });

  const openPanel = (panel) => {
    if (panel.exclusive) {
      setPanelOpen([panel.id]);
    } else {
      setPanelOpen((prev) =>
        prev.filter((m) => m !== panel.id).concat(panel.id),
      );
    }
  };

  const closePanel = (panelid) => {
    setPanelOpen((prev) => prev.filter((m) => m !== panelid));
  };

  onMount(() => {
    // Initialise open state from spec once — event bus owns runtime state.
    if (spec.panels?.length) {
      const initially = spec.panels.filter((p) => p.open).map((p) => p.id);
      if (initially.length) setPanelOpen(initially);
    }
    triggerEvent({
      action: 'status',
      source: 'eidos',
      data: { code: 1, message: 'ready' },
    });
  });

  createEffect(
    on(lastEvent, () => {
      //console.log('Event triggered');
      if (spec.panels && spec.panels.length > 0) {
        spec.panels.forEach((panel) => {
          if (matchesAnyTrigger(lastEvent(), panel.trigger)) {
            openPanel(panel);
          }
        });
        if (lastEvent()?.action === 'openPanel' && lastEvent()?.data?.panelId) {
          const panel = spec.panels.find(
            (p) => p.id === lastEvent().data.panelId,
          );
          if (panel) openPanel(panel);
        }
        if (
          lastEvent()?.action === 'closePanel' &&
          lastEvent()?.data?.panelId
        ) {
          closePanel(lastEvent().data.panelId);
        }
      }
    }),
  );

  // Spec-driven open/close: a specPatch to panel.open drives state without
  // writing back (no feedback loop — closePanel no longer patches spec).
  // defer:true so onMount handles initial state and this only fires on changes.
  createEffect(
    on(
      () => specTrack(spec.panels),
      () => {
        if (!spec.panels?.length) return;
        spec.panels.forEach((panel) => {
          if (panel.open && !panelOpen().includes(panel.id)) {
            openPanel(panel);
          } else if (!panel.open && panelOpen().includes(panel.id)) {
            closePanel(panel.id);
          }
        });
      },
      { defer: true },
    ),
  );

  return (
    <ThemeProvider theme={spec.theme || {}}>
      <For each={spec.panels}>
        {(panel, i) => (
          <PanelNode
            id={panel.id}
            node={panel.node}
            title={panel.title}
            width={panel.width}
            height={panel.height}
            position={panel.position}
            closable={panel.closable}
            onClose={() => closePanel(panel.id)}
            onOpen={() => openPanel(panel.id)}
            open={panelOpen().includes(panel.id)}
          />
        )}
      </For>
      <Show when={spec.title || spec.logo}>
        <HeaderBar title={spec.title} logo={spec.logo} />
      </Show>
      <Show when={rootError()}>
        <div
          role="alert"
          style={{
            padding: '1rem',
            color: '#c33',
            'font-family': 'monospace',
          }}
        >
          {rootError()}
        </div>
      </Show>
      <Show when={!rootError() && spec.root.id} keyed>
        <div
          style={{
            width: '100%',
            height: `calc( 100% - ${spec.title || spec.logo ? 50 : 0}px )`,
            display: 'flex',
          }}
        >
          <Node
            id={spec.root.id}
            path="/root"
            nodeSpec={spec.root}
            nodeType={spec.root.nodeType}
          />
        </div>
      </Show>
    </ThemeProvider>
  );
};

export const Renderer = ({ specOrigins, id }) => {
  const [auth, setAuth] = createSignal(null);
  let wrapperRef;
  const handleMessage = (ev) => {
    console.log('received message', ev.data);
    if (!ev.data || !ev.data.type || !ev.data.id || ev.data.id != id) {
      console.log('ignoring message', ev.data);
      return;
    }
    try {
      if (ev.data.type === 'spec') {
        //console.log('received spec', ev.data.payload);
        specUpdate(ev.data.payload);
      } else if (ev.data.type === 'patch') {
        //console.log('received patch', ev.data.payload);
        specPatch(ev.data.payload);
      } else if (ev.data.type === 'auth') {
        //console.log('received auth', ev.data.payload);
        setAuth(ev.data.payload);
      } else if (ev.data.type === 'event') {
        triggerEvent(ev.data.payload);
      }
    } catch (ex) {
      console.error(ex);
    }
  };
  window.addEventListener('message', handleMessage);

  onCleanup(() => {
    window.removeEventListener('message', handleMessage);
  });

  return (
    <DataProvider
      authToken={auth}
      datameshService={DATAMESH_SERVICE}
      gatewayService={GATEWAY_SERVICE}
    >
      <EventBusProvider>
        <div
          className={classes.wrapper}
          ref={wrapperRef}
          //onContextMenu={(e) => e.preventDefault()}
        >
          <Eidos id={id} />
        </div>
      </EventBusProvider>
    </DataProvider>
  );
};
